function createStickyGroupingMixin(options) {
  const { groupingField, stickyFiltersField, dashboardDataField, groupingConfigMap, fetchDashboardMethod, loadGroupingMethod, filterRowsMethod } = options

  return {
    data() {
      return {
        pendingSelectedGrouping: [],
        openFilterEditorGrouping: null,
        filterEditorLoading: false,
        dropDownGroups: [],
        filterGroupSelectionSource: {
          grouping: null,
          requestKey: null,
          dashboard: null,
          promise: null
        }
      }
    },
    computed: {
      hasStickyGroupingFilters() {
        return !!this.__getStickyGroupingFilters().length
      },
      currentGroupingValue() {
        return this.__getCurrentGroupingValue()
      },
      currentGroupingConfig() {
        return this.getGroupingConfig(this.__getCurrentGroupingValue())
      },
      currentGroupingStickyFilter() {
        return this.getStickyGroupingFilterByGrouping(this.__getCurrentGroupingValue())
      },
      editingGrouping() {
        return this.openFilterEditorGrouping || this.__getCurrentGroupingValue()
      },
      editingGroupingConfig() {
        return this.getGroupingConfig(this.editingGrouping)
      },
      editingGroupingStickyFilter() {
        return this.getStickyGroupingFilterByGrouping(this.editingGrouping)
      },
      otherStickyGroupingFilters() {
        return this.sortStickyGroupingFilters(this.getStickyGroupingFiltersExcludingGrouping(this.__getCurrentGroupingValue()), 'desc')
      }
    },
    methods: {
      // __ indicates a private method by naming convention only since its JS
      __getCurrentGroupingValue() {
        return this[groupingField]
      },
      __getStickyGroupingFilters() {
        return this[stickyFiltersField] || []
      },
      __setStickyGroupingFilters(filters) {
        this[stickyFiltersField] = filters
      },
      __getCurrentDashboardData() {
        const dashboardData = this[dashboardDataField]
        if (Array.isArray(dashboardData)) {
          return dashboardData.length ? dashboardData[0] : null
        }

        return dashboardData || null
      },
      __filterStickyScopeRows(rows, grouping, dashboardData = null) {
        if (!filterRowsMethod || typeof this[filterRowsMethod] !== 'function') {
          return rows
        }

        return this[filterRowsMethod](rows, grouping, dashboardData)
      },
      __fetchStickyGroupingDashboard(grouping, selectedAccountNumbers = null) {
        return this[fetchDashboardMethod](grouping, selectedAccountNumbers)
      },
      __loadStickyGrouping(grouping, mainClientResponseOverride) {
        return this[loadGroupingMethod](grouping, mainClientResponseOverride)
      },
      parseAccountNumbers(accountNumbers) {
        if (Array.isArray(accountNumbers)) {
          return accountNumbers.filter(x => Number.isInteger(x))
        }

        if (!accountNumbers || typeof accountNumbers !== 'string') {
          return []
        }

        return [
          ...new Set(
            accountNumbers
              .split(',')
              .map(x => parseInt(x, 10))
              .filter(x => !isNaN(x))
          )
        ]
      },
      getGroupingConfig(grouping = this.__getCurrentGroupingValue()) {
        return groupingConfigMap[grouping] || null
      },
      getLargeStatRows(title, dashboardData = null) {
        const sourceDashboard = dashboardData || this.__getCurrentDashboardData()
        if (!sourceDashboard || !sourceDashboard.largeStats) {
          return []
        }

        let groupIndex = sourceDashboard.largeStats.findIndex(x => x.title === title)
        return sourceDashboard.largeStats.some(x => x.title === title) ? sourceDashboard.largeStats[groupIndex].data : []
      },
      getLargeStatGroupList(title, dashboardData = null) {
        const sourceDashboard = dashboardData || this.__getCurrentDashboardData()
        if (!sourceDashboard || !sourceDashboard.largeStats) {
          return []
        }

        let groupIndex = sourceDashboard.largeStats.findIndex(x => x.title === title)
        return sourceDashboard.largeStats.some(x => x.title === title) && sourceDashboard.largeStats[groupIndex].groupList ? sourceDashboard.largeStats[groupIndex].groupList : []
      },
      getStickyScopeSourceRows(grouping = this.__getCurrentGroupingValue(), dashboardData = null) {
        const config = this.getGroupingConfig(grouping)
        if (!config) {
          return { config: null, rows: [] }
        }

        return {
          config,
          rows: this.__filterStickyScopeRows(this.getLargeStatRows(config.title, dashboardData), grouping, dashboardData)
        }
      },
      extractRowAccountNumbers(row) {
        const accountFields = ['accountNumbers', 'noChangeAccounts', 'increasesAccounts', 'decreasesAccounts', 'noRecAccounts', 'additionsAccounts', 'deletionsAccounts']
        const response = new Set()

        accountFields.forEach(field => {
          this.parseAccountNumbers(row && row[field]).forEach(id => response.add(id))
        })

        return [...response]
      },
      buildStickyGroupingFilterFromSelection(selectedValues = this.selectedGrouping, grouping = this.__getCurrentGroupingValue(), dashboardData = null, existingFilter = null) {
        if (!selectedValues.length) {
          return null
        }

        const { config, rows } = this.getStickyScopeSourceRows(grouping, dashboardData)
        if (!config || !rows.length) {
          return null
        }

        const normalizedValues = selectedValues.map(x => x.toString())
        const matchedRows = rows.filter(row => row[config.key] != null && normalizedValues.includes(row[config.key].toString()))
        const selectedAccountNumbers = [...new Set(matchedRows.flatMap(row => this.extractRowAccountNumbers(row)))]

        if (!selectedAccountNumbers.length) {
          return null
        }

        const groupOptions = this.getFilterGroupOptions(grouping, dashboardData)
        const labels = normalizedValues.map(value => {
          const groupOption = groupOptions.find(option => option.key.toString() === value)
          return groupOption ? groupOption.value : value
        })

        return {
          grouping,
          groupKey: config.key,
          groupingLabel: config.label,
          selectedValues: normalizedValues,
          labels,
          selectedAccountNumbers,
          createdOrder: existingFilter && Number.isInteger(existingFilter.createdOrder) ? existingFilter.createdOrder : this.getNextStickyGroupingCreatedOrder()
        }
      },
      areGroupingSelectionsEqual(left, right) {
        if (left.length !== right.length) {
          return false
        }

        return left.every((value, index) => value.toString() === right[index].toString())
      },
      getCombinedStickyAccountNumbersForFilters(filters = this.__getStickyGroupingFilters()) {
        if (!filters || !filters.length) {
          return null
        }

        let combined = new Set(filters[0].selectedAccountNumbers || [])

        filters.slice(1).forEach(filter => {
          const filterIds = new Set(filter.selectedAccountNumbers || [])
          combined = new Set([...combined].filter(id => filterIds.has(id)))
        })

        return combined.size ? [...combined] : []
      },
      getCombinedStickyAccountNumbers() {
        return this.getCombinedStickyAccountNumbersForFilters(this.__getStickyGroupingFilters())
      },
      getStickyGroupingFilterByGrouping(grouping = this.__getCurrentGroupingValue()) {
        return this.__getStickyGroupingFilters().find(filter => filter.grouping === grouping) || null
      },
      getStickyGroupingFiltersExcludingGrouping(grouping = this.__getCurrentGroupingValue(), filters = this.__getStickyGroupingFilters()) {
        return filters.filter(filter => filter.grouping !== grouping)
      },
      getNextStickyGroupingCreatedOrder(filters = this.__getStickyGroupingFilters()) {
        if (!filters || !filters.length) {
          return 1
        }

        return Math.max(...filters.map(filter => (Number.isInteger(filter.createdOrder) ? filter.createdOrder : 0))) + 1
      },
      sortStickyGroupingFilters(filters = this.__getStickyGroupingFilters(), direction = 'asc') {
        const sortedFilters = [...filters].sort((left, right) => {
          const leftOrder = Number.isInteger(left.createdOrder) ? left.createdOrder : 0
          const rightOrder = Number.isInteger(right.createdOrder) ? right.createdOrder : 0
          return leftOrder - rightOrder
        })

        return direction === 'desc' ? sortedFilters.reverse() : sortedFilters
      },
      getStickyGroupingSelectionValues(grouping = this.__getCurrentGroupingValue()) {
        const groupingFilter = this.getStickyGroupingFilterByGrouping(grouping)
        return groupingFilter ? [...groupingFilter.selectedValues] : []
      },
      setCurrentGroupingSelections(grouping = this.__getCurrentGroupingValue()) {
        this.selectedGrouping = this.getStickyGroupingSelectionValues(grouping)
      },
      setFilterEditorSelections(grouping = this.editingGrouping) {
        this.pendingSelectedGrouping = this.getStickyGroupingSelectionValues(grouping)
      },
      getActiveFilterEditorGrouping() {
        return this.editingGrouping
      },
      isFilterEditorVisibleForGrouping(grouping = this.__getCurrentGroupingValue()) {
        return this.openFilterEditorGrouping === grouping
      },
      getFilterGroupSelectionSourceDashboard(grouping = this.__getCurrentGroupingValue()) {
        return this.filterGroupSelectionSource.grouping === grouping ? this.filterGroupSelectionSource.dashboard : null
      },
      resetFilterGroupSelectionSource() {
        this.filterGroupSelectionSource = {
          grouping: null,
          requestKey: null,
          dashboard: null,
          promise: null
        }
      },
      async refreshFilterGroupSelectionSource(grouping = this.__getCurrentGroupingValue()) {
        const currentGroupingFilter = this.getStickyGroupingFilterByGrouping(grouping)
        if (!currentGroupingFilter) {
          this.resetFilterGroupSelectionSource()
          return null
        }

        const selectedAccountNumbers = this.getCombinedStickyAccountNumbersForFilters(this.getStickyGroupingFiltersExcludingGrouping(grouping))
        const normalizedScope = Array.isArray(selectedAccountNumbers) ? [...selectedAccountNumbers].sort((left, right) => left - right) : []
        const requestKey = `${grouping}:${normalizedScope.join(',')}`
        const source = this.filterGroupSelectionSource

        if (source.grouping === grouping && source.requestKey === requestKey) {
          if (source.dashboard) {
            return source.dashboard
          }

          if (source.promise) {
            return source.promise
          }
        }

        const dashboardPromise = this.__fetchStickyGroupingDashboard(grouping, selectedAccountNumbers)
        this.filterGroupSelectionSource = {
          grouping,
          requestKey,
          dashboard: null,
          promise: dashboardPromise
        }

        try {
          const dashboardData = await dashboardPromise
          if (this.filterGroupSelectionSource.grouping === grouping && this.filterGroupSelectionSource.requestKey === requestKey) {
            this.filterGroupSelectionSource.dashboard = dashboardData
          }
          return dashboardData
        } finally {
          if (this.filterGroupSelectionSource.grouping === grouping && this.filterGroupSelectionSource.requestKey === requestKey) {
            this.filterGroupSelectionSource.promise = null
          }
        }
      },
      formatStickyGroupingFilter(filter) {
        return `${filter.groupingLabel}: ${filter.labels.join(', ')}`
      },
      getFilterGroupOptions(grouping = this.__getCurrentGroupingValue(), dashboardData = null) {
        const config = this.getGroupingConfig(grouping)
        if (!config) {
          return []
        }

        return this.getLargeStatGroupList(config.title, dashboardData)
      },
      getStickyGroupingRebuildPlan(filters = this.__getStickyGroupingFilters(), changedGrouping = null, previousFilters = this.__getStickyGroupingFilters()) {
        const orderedFilters = this.sortStickyGroupingFilters(filters)
        const previousOrderedFilters = this.sortStickyGroupingFilters(previousFilters)
        if (!orderedFilters.length || !changedGrouping) {
          return {
            orderedFilters,
            preservedFilters: [],
            filtersToRebuild: orderedFilters,
            startingScope: null
          }
        }

        const nextIndex = orderedFilters.findIndex(filter => filter.grouping === changedGrouping)
        const previousIndex = previousOrderedFilters.findIndex(filter => filter.grouping === changedGrouping)
        const rebuildStartIndex = nextIndex > -1 ? nextIndex : previousIndex
        if (rebuildStartIndex <= 0) {
          return {
            orderedFilters,
            preservedFilters: [],
            filtersToRebuild: orderedFilters,
            startingScope: null
          }
        }

        const preservedFilters = previousOrderedFilters.slice(0, rebuildStartIndex).filter(filter => orderedFilters.some(nextFilter => nextFilter.grouping === filter.grouping))

        return {
          orderedFilters,
          preservedFilters,
          filtersToRebuild: orderedFilters.slice(rebuildStartIndex),
          startingScope: this.getCombinedStickyAccountNumbersForFilters(preservedFilters)
        }
      },
      async rebuildStickyGroupingFilters(filters = this.__getStickyGroupingFilters(), activeGrouping = this.__getCurrentGroupingValue(), seedDashboards = {}, rebuildPlan = null) {
        const plan = rebuildPlan || this.getStickyGroupingRebuildPlan(filters, activeGrouping)
        const rebuiltFilters = [...plan.preservedFilters]
        let currentScope = plan.startingScope
        const fetchedDashboards = {}

        for (const filter of plan.filtersToRebuild) {
          const hasSeedDashboard = Object.prototype.hasOwnProperty.call(seedDashboards, filter.grouping)
          const dashboardData = hasSeedDashboard ? seedDashboards[filter.grouping] : await this.__fetchStickyGroupingDashboard(filter.grouping, currentScope)
          fetchedDashboards[filter.grouping] = dashboardData
          const rebuiltFilter = this.buildStickyGroupingFilterFromSelection(filter.selectedValues, filter.grouping, dashboardData, filter)

          if (!rebuiltFilter) {
            break
          }

          rebuiltFilters.push(rebuiltFilter)
          currentScope = [...rebuiltFilter.selectedAccountNumbers]
        }

        let finalScopedDashboard = null
        if (activeGrouping) {
          finalScopedDashboard = Object.prototype.hasOwnProperty.call(fetchedDashboards, activeGrouping) ? fetchedDashboards[activeGrouping] : await this.__fetchStickyGroupingDashboard(activeGrouping, currentScope)
        }
        this.__setStickyGroupingFilters(rebuiltFilters)
        return { currentScope, fetchedDashboards, finalScopedDashboard }
      },
      async clearStickyGroupingFilter(grouping) {
        const previousFilters = this.__getStickyGroupingFilters()
        const nextFilters = this.__getStickyGroupingFilters().filter(filter => filter.grouping !== grouping)
        const rebuildPlan = this.getStickyGroupingRebuildPlan(nextFilters, grouping, previousFilters)
        this.loading = true
        this.__setStickyGroupingFilters(nextFilters)
        this.setCurrentGroupingSelections()
        this.filterGroupUpdate()
        const rebuildResult = await this.rebuildStickyGroupingFilters(nextFilters, this.__getCurrentGroupingValue(), {}, rebuildPlan)
        this.setCurrentGroupingSelections()
        this.filterGroupUpdate()
        this.__loadStickyGrouping(this.__getCurrentGroupingValue(), rebuildResult.finalScopedDashboard)
      },
      handleFilterEditorVisibilityChange(grouping, visible) {
        if (visible) {
          if (this.openFilterEditorGrouping === grouping) {
            return
          }
          this.handleFilterEditorOpen(grouping)
          return
        }

        if (this.openFilterEditorGrouping === grouping) {
          this.handleFilterEditorClose(grouping)
        }
      },
      getFilterGroupRefName(grouping = this.getActiveFilterEditorGrouping()) {
        return `filterGroup_${grouping}`
      },
      openFilterGroupMenu(grouping = this.getActiveFilterEditorGrouping()) {
        const refName = this.getFilterGroupRefName(grouping)
        const filterGroupRef = Array.isArray(this.$refs[refName]) ? this.$refs[refName][0] : this.$refs[refName]
        if (!filterGroupRef) {
          return
        }

        if (filterGroupRef.focus) {
          filterGroupRef.focus()
        }

        if (typeof filterGroupRef.toggleMenu === 'function') {
          if (!filterGroupRef.visible) {
            filterGroupRef.toggleMenu()
          }
          return
        }

        if (Object.prototype.hasOwnProperty.call(filterGroupRef, 'visible')) {
          filterGroupRef.visible = true
        }
      },
      async applyPendingFilterGroupChanges(grouping = this.getActiveFilterEditorGrouping()) {
        const existingSelections = this.getStickyGroupingSelectionValues(grouping)
        if (this.areGroupingSelectionsEqual(this.pendingSelectedGrouping, existingSelections)) {
          this.resetFilterGroupSelectionSource()
          this.dropDownGroups = this.groupDropDown()
          return
        }

        const currentGrouping = this.__getCurrentGroupingValue()
        const groupingToEdit = grouping
        const previousFilters = this.__getStickyGroupingFilters()
        const existingFilter = this.getStickyGroupingFilterByGrouping(groupingToEdit)
        const sourceDashboard = this.getFilterGroupSelectionSourceDashboard(groupingToEdit)
        const stickyGroupingFilter = this.buildStickyGroupingFilterFromSelection(this.pendingSelectedGrouping, groupingToEdit, sourceDashboard, existingFilter)
        this.resetFilterGroupSelectionSource()
        const nextFilters = this.__getStickyGroupingFilters().filter(filter => filter.grouping !== groupingToEdit)
        if (stickyGroupingFilter) {
          nextFilters.push(stickyGroupingFilter)
        }

        const orderedNextFilters = this.sortStickyGroupingFilters(nextFilters)
        const rebuildPlan = this.getStickyGroupingRebuildPlan(orderedNextFilters, groupingToEdit, previousFilters)
        const seedDashboards = {}
        if (sourceDashboard) {
          seedDashboards[groupingToEdit] = sourceDashboard
        } else if (stickyGroupingFilter && !existingFilter && groupingToEdit === currentGrouping) {
          seedDashboards[groupingToEdit] = this.__getCurrentDashboardData()
        }
        this.loading = true
        this.__setStickyGroupingFilters(orderedNextFilters)
        this.setCurrentGroupingSelections(currentGrouping)
        this.setFilterEditorSelections(groupingToEdit)
        const rebuildResult = await this.rebuildStickyGroupingFilters(orderedNextFilters, currentGrouping, seedDashboards, rebuildPlan)
        this.setCurrentGroupingSelections(currentGrouping)
        this.setFilterEditorSelections(groupingToEdit)

        if (this.selectedGrouping.length === 0) {
          this.filterGroupUpdate()
        }

        this.__loadStickyGrouping(currentGrouping, rebuildResult.finalScopedDashboard)
      },
      async handleFilterEditorOpen(grouping = this.__getCurrentGroupingValue()) {
        this.openFilterEditorGrouping = grouping
        this.setFilterEditorSelections(grouping)
        this.filterEditorLoading = true
        try {
          await this.refreshFilterGroupSelectionSource(grouping)
          this.dropDownGroups = this.getFilterGroupOptions(grouping, this.getFilterGroupSelectionSourceDashboard(grouping))
          this.$nextTick(() => {
            this.openFilterGroupMenu(grouping)
          })
        } finally {
          this.filterEditorLoading = false
        }
      },
      async handleFilterEditorClose(grouping = this.getActiveFilterEditorGrouping()) {
        await this.applyPendingFilterGroupChanges(grouping)
        if (this.openFilterEditorGrouping === grouping) {
          this.openFilterEditorGrouping = null
        }
      },
      clearCurrentGroupingFilterFromPopover() {
        this.pendingSelectedGrouping = []
        this.handleFilterEditorClose(this.getActiveFilterEditorGrouping())
      },
      clearCurrentGroupingFilterQuick() {
        this.openFilterEditorGrouping = null
        this.resetFilterGroupSelectionSource()
        this.pendingSelectedGrouping = []
        this.selectedGrouping = []
        this.clearStickyGroupingFilter(this.__getCurrentGroupingValue())
      }
    }
  }
}

export default createStickyGroupingMixin
